'use client';

import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import {
  appointmentFilterSurfaceActiveAtom,
  selectedDepartmentIdsFilterAtom,
  selectedProviderGroupIdsFilterAtom,
} from './atoms';

type IntegrationFilterSelection = {
  selectedGroupIds: string[];
  selectedDepartmentIds: string[];
};

/** Builds repeatable schedule `filter` query values for ml-scribe. */
export function buildIntegrationFilterTags({
  selectedGroupIds,
  selectedDepartmentIds,
}: IntegrationFilterSelection): string[] {
  return [
    ...selectedGroupIds.map((id) => `provider_group:${id}`),
    ...selectedDepartmentIds.map((id) => `department:${id}`),
  ];
}

export function useIntegrationFilterTags(): string[] {
  const selectedGroupIds = useAtomValue(selectedProviderGroupIdsFilterAtom);
  const selectedDepartmentIds = useAtomValue(selectedDepartmentIdsFilterAtom);

  return useMemo(
    () => buildIntegrationFilterTags({ selectedGroupIds, selectedDepartmentIds }),
    [selectedGroupIds, selectedDepartmentIds]
  );
}

export function useClearIntegrationFilters() {
  const setSelectedGroupIds = useSetAtom(selectedProviderGroupIdsFilterAtom);
  const setSelectedDepartmentIds = useSetAtom(selectedDepartmentIdsFilterAtom);

  return useCallback(() => {
    setSelectedGroupIds([]);
    setSelectedDepartmentIds([]);
  }, [setSelectedGroupIds, setSelectedDepartmentIds]);
}

/**
 * Schedule filters for ml-scribe — gated on the rollout flag and active
 * selection, not EHR provider identity. The flag is a FOREIGN domain's
 * (`@heidi/integrations/flags`), so the caller resolves it and passes it in.
 */
export function useScheduleIntegrationFilters(
  isMultiProviderScheduleEnabled: boolean
): string[] | undefined {
  const isAppointmentSurfaceActive = useAtomValue(appointmentFilterSurfaceActiveAtom);
  const tags = useIntegrationFilterTags();

  return isAppointmentSurfaceActive && isMultiProviderScheduleEnabled && tags.length > 0
    ? tags
    : undefined;
}
